import ConfirmDialog from '@components/Shared/ConfirmDialog';
import FileUpload from '@components/Shared/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/UploadFile';
import {
  Alert, Box, Button, Card, CardContent, IconButton, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import {
  useGetLicenseFilesQuery, useImportLicenseFileMutation, useRemoveLicenseFileMutation,
  type LicenseFile
} from '@store/api/licenseFileEndpoints';
import { serverMessage } from '@store/error';
import { useState } from 'react';

const formatDate = (value?: string) => value ? new Date(value).toFormattedDateTimeString() : '-';

/**
 * Importing and removing the signed `.suparlic` files entitlements arrive in.
 *
 * It lives on the Licenses page rather than with enrolment because a licence file is **content**,
 * not setup: one arrives whenever the commercial terms change, long after the server was set up,
 * and renewing a certificate must not force reissuing licences. Keeping it next to enrolment made
 * the enrolment page show an import control that could not work yet.
 */
const LicenseFilesCard = () => {
  const { data: licenseFiles = [] } = useGetLicenseFilesQuery();
  const [importLicenseFile, { isLoading: isImporting }] = useImportLicenseFileMutation();
  const [removeLicenseFile] = useRemoveLicenseFileMutation();

  const [importError, setImportError] = useState<string>();
  const [importSummary, setImportSummary] = useState<string>();
  const [removing, setRemoving] = useState<LicenseFile>();

  const onImport = async (file?: File) => {
    if (!file) return;

    setImportError(undefined);
    setImportSummary(undefined);

    try {
      const result = await importLicenseFile(file).unwrap();

      setImportSummary(
        `${result.imported.length.formatCount('license', 'licenses')} added, ${result.updated.length} renewed` +
        // Worth spelling out: a superseded import looks like nothing happened otherwise.
        (result.superseded.length > 0
          ? `, ${result.superseded.length} left alone because this file is older than the one installed`
          : '') +
        (result.creditLowered.length > 0
          ? `, offline credit lowered on ${result.creditLowered.length} to fit this file`
          : '') +
        (result.seatTimeRaised.length > 0
          ? `, seat time raised on ${result.seatTimeRaised.length} to this file's`
          : ''));
    } catch (error) {
      setImportError(serverMessage(error) ?? 'The license file could not be imported.');
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant='h6' gutterBottom>License files</Typography>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Seats, features and expiry come from these files and from nowhere else. Each one is
          encrypted to this installation and signed by CDMVision, so a file issued for another
          server cannot be opened here at all.
        </Typography>

        <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 2 }}>
          <FileUpload accept='.suparlic,application/json' onFileChange={onImport}>
            <Button variant='contained' startIcon={<UploadIcon />} disabled={isImporting} component='span'>
              Import license file
            </Button>
          </FileUpload>
        </Stack>

        {importError && <Alert severity='error' sx={{ mb: 2 }}>{importError}</Alert>}
        {importSummary && <Alert severity='success' sx={{ mb: 2 }}>{importSummary}</Alert>}

        {licenseFiles.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>No license files imported.</Typography>
        ) : (
          // Scrolls sideways on a narrow screen rather than cutting off the columns on the right.
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell align='right'>Serial</TableCell>
                  <TableCell>Issued</TableCell>
                  <TableCell>Imported</TableCell>
                  <TableCell align='right'>Licenses</TableCell>
                  <TableCell align='right' />
                </TableRow>
              </TableHead>
              <TableBody>
                {licenseFiles.map(file => (
                  <TableRow key={file.fileId}>
                    <TableCell>
                      {file.customer.organizationName}
                      <Typography variant='caption' display='block' color='text.secondary'>
                        {file.fileId}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>{file.fileSerial}</TableCell>
                    <TableCell>{formatDate(file.issuedAt)}</TableCell>
                    <TableCell>{formatDate(file.importedAt)}</TableCell>
                    <TableCell align='right'>{file.licenseIds.length}</TableCell>
                    <TableCell align='right'>
                      <IconButton
                        size='small'
                        aria-label='Remove license file'
                        onClick={() => setRemoving(file)}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>

      {removing && (
        <ConfirmDialog
          title='Remove this license file?'
          confirmLabel='Remove file'
          onClose={() => setRemoving(undefined)}
          onConfirm={() => removeLicenseFile(removing.fileId)}
        >
          The {removing.licenseIds.length} license
          {removing.licenseIds.length === 1 ? '' : 's'} this file carries
          {removing.licenseIds.length === 1 ? ' is' : ' are'} checked against the other files
          installed here. One that another file still grants keeps working on that file&apos;s
          terms. One that nothing grants any more is retired: its seats are dropped and its
          clients stop being served within fifteen minutes.
          {' '}Activation ids, passwords and labels are kept, so importing this file again brings
          those licenses back without reissuing credentials to anybody.
        </ConfirmDialog>
      )}
    </Card>
  );
};

export default LicenseFilesCard;
